import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { categories } from "@/mocks/categories";
import { articles, type ArticleSummary } from "@/mocks/articles";

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState<string>(slug || "muban-sucai");

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      navigate(`/category/${slug}`, { replace: true });
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [slug, navigate]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles: ArticleSummary[] = articles
    .filter((a) => a.categorySlug === activeCategory)
    .map(({ id, title, excerpt, categorySlug, categoryName, author, date, coverImage, tags, readTime }) => ({
      id, title, excerpt, categorySlug, categoryName, author, date, coverImage, tags, readTime,
    }));

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${currentCategory?.name || ""} - 黄油网站`,
    "description": `浏览黄油网站${currentCategory?.name || ""}分类下的所有文章与资源`,
    "url": `https://huangyou.design/category/${activeCategory}`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://huangyou.design/" },
        { "@type": "ListItem", "position": 2, "name": currentCategory?.name || "" },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}></script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Header */}
          <section className="w-full py-10 md:py-14 bg-background-50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb items={[
                { label: t("article.breadcrumb_home"), to: "/" },
                { label: currentCategory?.name || "" },
              ]} />
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mt-4 md:mt-6" style={{ fontFamily: "var(--font-heading)" }}>
                {currentCategory?.name || t("category.title")}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 mt-2">
                {currentCategory?.description || ""}
              </p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto px-4 md:px-6 overflow-x-auto">
              <div className="flex items-center gap-1 py-3 min-w-max">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all duration-200 cursor-pointer border-none ${
                      activeCategory === cat.slug
                        ? "bg-accent-100 text-accent-900 font-medium"
                        : "text-foreground-500 hover:text-foreground-800 hover:bg-background-100"
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Articles */}
          <section className="w-full py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all duration-300 no-underline cursor-pointer flex flex-col"
                    >
                      <div className="relative w-full aspect-[16/10] overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <span className="absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-700">
                          {article.readTime}
                        </span>
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <span className="text-xs text-foreground-500 mb-2">{article.categoryName}</span>
                        <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-foreground-800 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3 flex-1">
                          {article.excerpt}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-foreground-400 mt-auto pt-3 border-t border-background-200/70">
                          <span>{article.author}</span>
                          <span>·</span>
                          <span>{article.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full mb-4" style={{ backgroundColor: "oklch(var(--secondary-100))" }}>
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">{t("category.no_articles")}</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}